import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  fetchCustomerDuplicates,
  listCustomerMerges,
  undoCustomerMerge,
  type DuplicateGroup,
  type DuplicateStrategy,
} from '@/lib/api/customers.js';
import { formatCurrency, formatDate } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';
import { cn } from '@/lib/utils.js';
import { MergeCustomersModal } from '@/components/customers/MergeCustomersModal.js';

export function DuplicateCustomersSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const isTenantAdmin = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'scan' | 'history'>('scan');
  const [strategy, setStrategy] = useState<DuplicateStrategy>('all');
  const [minConfidence, setMinConfidence] = useState<'high' | 'medium' | 'low'>('low');
  const [enabled, setEnabled] = useState(false);
  const [reviewGroup, setReviewGroup] = useState<DuplicateGroup | null>(null);

  const q = useQuery({
    queryKey: ['customer-duplicates', strategy, minConfidence],
    queryFn: () => fetchCustomerDuplicates({ strategy, minConfidence, pageSize: 50 }),
    enabled,
  });

  const historyQ = useQuery({
    queryKey: ['customer-merges'],
    queryFn: listCustomerMerges,
    enabled: tab === 'history',
  });

  const undoMut = useMutation({
    mutationFn: (id: string) => undoCustomerMerge(id),
    onSuccess: () => {
      toastSuccess('Merge reversed');
      void queryClient.invalidateQueries({ queryKey: ['customer-merges'] });
      void queryClient.invalidateQueries({ queryKey: ['customer-duplicates'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const groups = useMemo(() => q.data?.data ?? [], [q.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-950">Duplicate Customers</h1>
        <p className="mt-1 text-sm text-slate-500">
          Find and merge duplicate customer records from imports or manual entry.
        </p>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          className={cn(
            'rounded-lg px-3 py-1.5 text-sm font-semibold',
            tab === 'scan' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700',
          )}
          onClick={() => setTab('scan')}
        >
          Scan
        </button>
        <button
          type="button"
          className={cn(
            'rounded-lg px-3 py-1.5 text-sm font-semibold',
            tab === 'history' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700',
          )}
          onClick={() => setTab('history')}
        >
          History
        </button>
      </div>

      {tab === 'scan' ? (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <label className="text-sm">
              Strategy
              <select
                className="mt-1 block rounded-lg border border-slate-300 px-2 py-1.5"
                value={strategy}
                onChange={(e) => setStrategy(e.target.value as DuplicateStrategy)}
              >
                <option value="all">All</option>
                <option value="exact_email">Exact email (high)</option>
                <option value="phone_and_full_name">Phone + same name (high)</option>
                <option value="phone_family">Phone + family last name (low)</option>
                <option value="phone_shared">Shared phone, different names (low)</option>
                <option value="name_and_address">Name + address</option>
                <option value="fuzzy_name">Fuzzy name only</option>
              </select>
            </label>
            <label className="text-sm">
              Min confidence
              <select
                className="mt-1 block rounded-lg border border-slate-300 px-2 py-1.5"
                value={minConfidence}
                onChange={(e) => setMinConfidence(e.target.value as 'high' | 'medium' | 'low')}
              >
                <option value="low">Low+</option>
                <option value="medium">Medium+</option>
                <option value="high">High only</option>
              </select>
            </label>
            <button
              type="button"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white"
              onClick={() => {
                setEnabled(true);
                void queryClient.invalidateQueries({ queryKey: ['customer-duplicates'] });
              }}
            >
              Scan for Duplicates
            </button>
          </div>

          {q.isFetching ? <p className="text-sm text-slate-500">Scanning…</p> : null}
          {q.isError ? (
            <p className="text-sm text-red-600">{parseApiError(q.error).message}</p>
          ) : null}

          <div className="space-y-4">
            {groups.map((g) => (
              <div
                key={g.groupId}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                      {g.confidence} confidence — {g.strategy.replace(/_/g, ' ')}{' '}
                      <span className="font-normal normal-case text-slate-500">{g.matchedOn}</span>
                    </p>
                    {g.warningLabel ? (
                      <p className="mt-1 text-xs font-semibold text-amber-800">{g.warningLabel}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white"
                    onClick={() => setReviewGroup(g)}
                  >
                    Review & Merge
                  </button>
                </div>
                <ul className="mt-3 space-y-2">
                  {g.customers.map((c) => (
                    <li key={c.id} className="text-sm text-slate-800">
                      <Link
                        to={`/customers/${c.id}`}
                        className="font-semibold text-primary hover:underline"
                      >
                        {c.firstName} {c.lastName}
                      </Link>{' '}
                      <span className="text-slate-500">
                        {c.email ?? '(no email)'} · {c.ticketCount} tickets
                      </span>
                      <div className="text-xs text-slate-500">
                        Created {formatDate(c.createdAt)}
                        {c.importedFrom ? ' · imported' : ''} · {formatCurrency(c.totalSpend)} ·{' '}
                        {c.loyaltyPoints} pts
                        {c.customerStatus === 'flagged' ? (
                          <span className="ml-2 font-semibold text-red-600">FLAGGED</span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {enabled && !q.isFetching && groups.length === 0 ? (
              <p className="text-sm text-slate-500">No duplicate groups found for this strategy.</p>
            ) : null}
          </div>
        </>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Absorbed</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {(historyQ.data ?? []).map((m) => (
                <tr key={m.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">{formatDate(m.mergedAt)}</td>
                  <td className="px-4 py-3">{m.absorbedCount} records</td>
                  <td className="px-4 py-3">{m.reason ?? '—'}</td>
                  <td className="px-4 py-3">
                    {m.reversedAt ? (
                      <span className="text-slate-500">Undone {formatDate(m.reversedAt)}</span>
                    ) : (
                      <span className="text-emerald-700">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isTenantAdmin && !m.reversedAt ? (
                      <button
                        type="button"
                        className="text-xs font-semibold text-red-700 hover:underline"
                        disabled={undoMut.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              'Undo this merge? Absorbed customers and FK references will be restored.',
                            )
                          ) {
                            undoMut.mutate(m.id);
                          }
                        }}
                      >
                        Undo
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {historyQ.isFetched && (historyQ.data ?? []).length === 0 ? (
            <p className="p-4 text-sm text-slate-500">No merges yet.</p>
          ) : null}
        </div>
      )}

      {reviewGroup ? (
        <MergeCustomersModal
          group={reviewGroup}
          onClose={() => setReviewGroup(null)}
          onMerged={() => {
            void queryClient.invalidateQueries({ queryKey: ['customer-duplicates'] });
            void queryClient.invalidateQueries({ queryKey: ['customer-merges'] });
          }}
        />
      ) : null}
    </div>
  );
}
